import React, { useEffect } from "react";
import _ from "lodash/fp";
import { GridGenerator } from "@actinc/dls/components";
import {
  Delete as DeleteIcon,
  DragHorizontalVariant as DragHorizontalVariantIcon,
  Plus as PlusIcon,
} from "@actinc/dls/icons/";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  ControlledFormInput,
  ControlledFormEditor,
  ControlledFormSelect,
} from "../../../../components";

import { useDraggableInPortal } from "../../../../services/hooks";
import { onDragEnd } from "../../../../services/utils";

const MatchInteractionForm = ({
  imageAssets = [],
  index,
  interaction,
  readonly,
}) => {
  const renderDraggable = useDraggableInPortal();
  const namePrefix = `item.interactions[${index}]`;
  // console.debug(
  //   "MatchInteractionForm - index %s, interaction %o",
  //   index,
  //   interaction,
  // );

  const { control, getValues, trigger } = useFormContext();

  const {
    append: appendElement,
    fields: elements,
    move: moveElement,
    remove: removeElement,
  } = useFieldArray({
    control,
    name: `${namePrefix}.elements`,
  });
  const {
    append: appendTarget,
    fields: targets,
    move: moveTarget,
    remove: removeTarget,
  } = useFieldArray({
    control,
    name: `${namePrefix}.targets`,
  });
  const {
    append: appendCorrectResponse,
    fields: correctResponse,
    remove: removeCorrectResponse,
  } = useFieldArray({
    control,
    name: `${namePrefix}.correctResponse`,
  });

  const addElement = () => {
    appendElement({
      content: "<p></p>",
      identifier: `Element${elements.length + 1}`,
      matchGroup: null,
      matchMax: 0,
    });
  };
  const addTarget = () => {
    appendTarget({
      identifier: `Target${targets.length + 1}`,
      matchGroup: null,
      matchMax: 0,
      text: "",
      width: 100,
    });
  };
  const addCorrectResponse = () => {
    appendCorrectResponse({
      element: "",
      target: "",
    });
  };
  const deleteElement = (i) => () => {
    removeElement(i);
  };
  const deleteCorrectResponse = (i) => () => {
    removeCorrectResponse(i);
  };
  const deleteTarget = (i) => () => {
    removeTarget(i);
  };

  if (!getValues(`${namePrefix}.identifier`)) return null;

  return (
    <Box
      sx={{
        alignItems: "self-start",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <ControlledFormSelect
        control={control}
        defaultValue={interaction.maxElementsPerRow}
        id={`${namePrefix}.maxElementsPerRow`}
        label="Elements Per Row"
        name={`${namePrefix}.maxElementsPerRow`}
        options={["All", 1, 2, 3, 4, 5].map((value) => ({
          label: value,
          value: value === "All" ? 0 : value,
        }))}
        readonly={readonly}
        rules={{
          required: true,
        }}
        trigger={() => trigger([`${namePrefix}.maxElementsPerRow`])}
      />
      <Box>
        <DragDropContext onDragEnd={onDragEnd(moveElement)}>
          <Droppable droppableId={`${interaction.identifier}-elements`}>
            {(droppableProvided) => {
              return (
                <List
                  dense={true}
                  disablePadding={true}
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {!!elements &&
                    elements.map((element, i) => {
                      return (
                        <Draggable
                          draggableId={element.id}
                          index={i}
                          isDragDisabled={readonly}
                          key={element.id}
                        >
                          {renderDraggable((draggableProvided) => (
                            <ListItem
                              dense={true}
                              disableGutters={true}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                            >
                              <Box>
                                <Box
                                  sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                    }}
                                  >
                                    <Box {...draggableProvided.dragHandleProps}>
                                      <DragHorizontalVariantIcon />
                                    </Box>
                                    <Typography variant="subtitle1">{`Element ${
                                      i + 1
                                    }`}</Typography>
                                  </Box>
                                  {!readonly && (
                                    <Tooltip title="Delete Element">
                                      <IconButton
                                        aria-label={`delete element ${i + 1}`}
                                        disabled={elements.length <= 1}
                                        onClick={deleteElement(i)}
                                        size="large"
                                        sx={{ padding: 1 }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                                <ControlledFormEditor
                                  assets={{ images: imageAssets }}
                                  control={control}
                                  id={`${namePrefix}.elements[${i}].content`}
                                  name={`${namePrefix}.elements[${i}].content`}
                                  onReady={(ed) => {
                                    console.info(
                                      `Item Interaction ${namePrefix} element ${i} content editor is ready, %o`,
                                      ed,
                                    );
                                  }}
                                  options={["blockMatch"]}
                                  readonly={readonly}
                                  trigger={() =>
                                    trigger([`${namePrefix}.elements`])
                                  }
                                  type="item"
                                />
                                <Box sx={{ marginTop: 2 }}>
                                  <ControlledFormInput
                                    control={control}
                                    label="Match Max"
                                    id={`${namePrefix}.elements[${i}].matchMax`}
                                    name={`${namePrefix}.elements[${i}].matchMax`}
                                    readonly={readonly}
                                    rules={{
                                      pattern: {
                                        message:
                                          "This field must contain a valid number between 0-99",
                                        value: /^[1-9][0-9]?$|^99$|^0$/,
                                      },
                                      required: true,
                                      validate: {
                                        mmCorrect: (v) => {
                                          if (v === 0 || v === "0") return;
                                          const element = getValues(
                                            `${namePrefix}.elements[${i}].content`,
                                          ).replace(/(<([^>]+)>)/gi, "");
                                          const selectedValLength = getValues(
                                            `${namePrefix}.correctResponse`,
                                          ).filter(
                                            (crs) => crs.element === element,
                                          ).length;
                                          return (
                                            v >= selectedValLength ||
                                            "The value must be greater than or equal to the number of times this element's content was selected as a correct response"
                                          );
                                        },
                                      },
                                    }}
                                    trigger={() =>
                                      trigger([
                                        `${namePrefix}.correctResponse[${i}].element`,
                                      ])
                                    }
                                  />
                                </Box>
                              </Box>
                            </ListItem>
                          ))}
                        </Draggable>
                      );
                    })}
                </List>
              );
            }}
          </Droppable>
        </DragDropContext>
      </Box>
      {!readonly && (
        <Button
          onClick={addElement}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Element
        </Button>
      )}
      <Box>
        <DragDropContext onDragEnd={onDragEnd(moveTarget)}>
          <Droppable droppableId={`${interaction.identifier}-targets`}>
            {(droppableProvided) => {
              return (
                <List
                  dense={true}
                  disablePadding={true}
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {!!targets &&
                    targets.map((target, i) => {
                      return (
                        <Draggable
                          draggableId={target.id}
                          index={i}
                          isDragDisabled={readonly}
                          key={target.id}
                        >
                          {renderDraggable((draggableProvided) => (
                            <ListItem
                              dense={true}
                              disableGutters={true}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                            >
                              <Box>
                                <Box
                                  sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                    }}
                                  >
                                    <Box {...draggableProvided.dragHandleProps}>
                                      <DragHorizontalVariantIcon />
                                    </Box>
                                    <Typography variant="subtitle1">{`Target ${
                                      i + 1
                                    }`}</Typography>
                                  </Box>
                                  {!readonly && (
                                    <Tooltip title="Delete Target">
                                      <IconButton
                                        aria-label={`delete target ${i + 1}`}
                                        disabled={targets.length <= 1}
                                        onClick={deleteTarget(i)}
                                        size="large"
                                        sx={{ padding: 1 }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                                <ControlledFormEditor
                                  assets={{ images: imageAssets }}
                                  control={control}
                                  id={`${namePrefix}.targets[${i}].text`}
                                  name={`${namePrefix}.targets[${i}].text`}
                                  onReady={(ed) => {
                                    console.info(
                                      `Item Interaction ${namePrefix} target ${i} content editor is ready, %o`,
                                      ed,
                                    );
                                  }}
                                  options={["blockMatch"]}
                                  readonly={readonly}
                                  trigger={() =>
                                    trigger([`${namePrefix}.targets`])
                                  }
                                  type="item"
                                />
                                <Box sx={{ marginTop: 2 }}>
                                  <ControlledFormInput
                                    control={control}
                                    label="Match Max"
                                    id={`${namePrefix}.targets[${i}].matchMax`}
                                    name={`${namePrefix}.targets[${i}].matchMax`}
                                    readonly={readonly}
                                    rules={{
                                      pattern: {
                                        message:
                                          "This field must contain a valid number between 0-99",
                                        value: /^[1-9][0-9]?$|^99$|^0$/,
                                      },
                                      required: true,
                                      validate: {
                                        mmCorrect: (v) => {
                                          if (v === 0 || v === "0") return;
                                          const targetValue = getValues(
                                            `${namePrefix}.targets[${i}].text`,
                                          ).replace(/(<([^>]+)>)/gi, "");
                                          const selectedValLength = getValues(
                                            `${namePrefix}.correctResponse`,
                                          ).filter(
                                            (crs) => crs.target === targetValue,
                                          ).length;
                                          return (
                                            v >= selectedValLength ||
                                            "The value must be greater than or equal to the number of times this target's content was selected as a correct response"
                                          );
                                        },
                                      },
                                    }}
                                    trigger={() =>
                                      trigger([
                                        `${namePrefix}.correctResponse[${i}].target`,
                                      ])
                                    }
                                  />
                                </Box>
                              </Box>
                            </ListItem>
                          ))}
                        </Draggable>
                      );
                    })}
                </List>
              );
            }}
          </Droppable>
        </DragDropContext>
      </Box>
      {!readonly && (
        <Button
          onClick={addTarget}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Target
        </Button>
      )}
      <Box aria-label="correct-match-order" sx={{ width: "100%" }}>
        <Typography sx={{ marginTop: 2 }} variant="subtitle1">
          Correct Responses
        </Typography>

        {!!correctResponse &&
          correctResponse.map((cr, i) => (
            <Box key={i}>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <GridGenerator
                  groups={[
                    [
                      {
                        children: (
                          <ControlledFormSelect
                            control={control}
                            defaultValue={cr?.target}
                            fullWidth={true}
                            id={`${namePrefix}.correctResponse[${i}].target`}
                            label="Target"
                            name={`${namePrefix}.correctResponse[${i}].target`}
                            options={getValues(`${namePrefix}.targets`).map(
                              ({ text = "", identifier }) => ({
                                label: text.replace(/(<([^>]+)>)/gi, ""),
                                value: identifier,
                              }),
                            )}
                            readonly={readonly}
                            rules={{
                              required: true,
                              validate: {
                                mmCorrect: (v) => {
                                  const selectedTargetContent = getValues(
                                    `${namePrefix}.targets`,
                                  ).filter((tc) => {
                                    return v === tc.identifier;
                                  });
                                  if (selectedTargetContent[0]?.matchMax === 0)
                                    return;
                                  const selectedValLength =
                                    correctResponse.filter(
                                      (crs) => crs.target === v,
                                    ).length;
                                  return (
                                    selectedTargetContent[0]?.matchMax >=
                                      selectedValLength ||
                                    "Must be less than or equal to the corresponding Max Match value"
                                  );
                                },
                              },
                            }}
                            trigger={() =>
                              trigger([`${namePrefix}.targets[${i}].matchMax`])
                            }
                          />
                        ),
                        key: `${cr.id}-target`,
                      },
                      {
                        children: (
                          <ControlledFormSelect
                            control={control}
                            defaultValue={cr?.element}
                            fullWidth={true}
                            id={`${namePrefix}.correctResponse[${i}].element`}
                            label="Element"
                            name={`${namePrefix}.correctResponse[${i}].element`}
                            options={getValues(`${namePrefix}.elements`).map(
                              ({ content = "", identifier }) => {
                                return {
                                  label: content.replace(/(<([^>]+)>)/gi, ""),
                                  value: identifier,
                                };
                              },
                            )}
                            readonly={readonly}
                            rules={{
                              required: true,
                              validate: {
                                mmCorrect: (v) => {
                                  const selectedElementContent = getValues(
                                    `${namePrefix}.elements`,
                                  ).filter((tc) => {
                                    return v === tc.identifier;
                                  });
                                  if (selectedElementContent[0]?.matchMax === 0)
                                    return;
                                  const selectedValLength =
                                    correctResponse.filter(
                                      (crs) => crs.element === v,
                                    ).length;
                                  return (
                                    selectedElementContent[0]?.matchMax >=
                                      selectedValLength ||
                                    "Must be less than or equal to the corresponding Max Match value"
                                  );
                                },
                              },
                            }}
                            trigger={() =>
                              trigger([`${namePrefix}.elements[${i}].matchMax`])
                            }
                          />
                        ),
                        key: `${cr.id}-element`,
                      },
                    ],
                  ]}
                />
                {!readonly && (
                  <Tooltip title="Delete Correct Response">
                    <IconButton
                      aria-label={`delete correct response ${i + 1}`}
                      disabled={correctResponse.length <= 1}
                      onClick={deleteCorrectResponse(i)}
                      size="large"
                      sx={{ padding: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        {!readonly && (
          <Button
            onClick={addCorrectResponse}
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Create Correct Response
          </Button>
        )}
      </Box>
    </Box>
  );
};

export { MatchInteractionForm };
